import { useState, type FormEvent } from "react";
import { api } from "../api";
import type { AppOptions, DealRecord, SearchFilters } from "../types";
import DealForm from "./DealForm";

function monthBounds(): {dateFrom: string; dateTo: string} {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const format = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return {dateFrom: format(first), dateTo: format(last)};
}

export default function SearchPanel({
  options,
  canManage,
  canViewRemoved,
  canRestore
}: {
  options: AppOptions;
  canManage: boolean;
  canViewRemoved: boolean;
  canRestore: boolean;
}) {
  const [filters, setFilters] = useState<SearchFilters>({
    ...monthBounds(),
    query: "",
    department: "",
    salespersonId: "",
    includeRemoved: false
  });
  const [deals, setDeals] = useState<DealRecord[]>([]);
  const [editing, setEditing] = useState<DealRecord | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function runSearch(event?: FormEvent) {
    event?.preventDefault();
    setLoading(true);
    setError("");
    try {
      setDeals(await api.searchDeals(filters));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Search failed.");
    } finally {
      setLoading(false);
    }
  }

  async function remove(deal: DealRecord) {
    const reason = window.prompt(`Why should ${deal.customer}'s deal be removed?`);
    if (!reason?.trim()) return;

    try {
      await api.removeDeal(crypto.randomUUID(), deal, reason.trim());
      await runSearch();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Removal failed.");
    }
  }

  async function restore(deal: DealRecord) {
    const reason = window.prompt(`Why should ${deal.customer}'s deal be restored?`);
    if (!reason?.trim()) return;

    try {
      await api.restoreDeal(crypto.randomUUID(), deal, reason.trim());
      await runSearch();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Restoration failed.");
    }
  }

  if (editing) {
    return (
      <DealForm
        options={options}
        existing={editing}
        onSaved={() => {setEditing(undefined); void runSearch();}}
        onCancel={() => setEditing(undefined)}
      />
    );
  }

  return (
    <section className="panel">
      <div className="panel-heading"><div><span className="eyebrow">Indexed PostgreSQL search</span><h2>Search Deal History</h2></div></div>

      <form className="search-form" onSubmit={runSearch}>
        <label>From<input type="date" value={filters.dateFrom} onChange={(e) => setFilters({...filters, dateFrom: e.target.value})} /></label>
        <label>To<input type="date" value={filters.dateTo} onChange={(e) => setFilters({...filters, dateTo: e.target.value})} /></label>
        <label>Department<select value={filters.department} onChange={(e) => setFilters({...filters, department: e.target.value as SearchFilters["department"]})}><option value="">All</option>{options.departments.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label>Salesperson<select value={filters.salespersonId} onChange={(e) => setFilters({...filters, salespersonId: e.target.value})}><option value="">All</option>{options.salespeople.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label className="search-keyword">Customer, stock, vehicle, deal ID, or name<input value={filters.query} onChange={(e) => setFilters({...filters, query: e.target.value})} placeholder="Start typing" /></label>
        {canViewRemoved && <label className="checkbox"><input type="checkbox" checked={filters.includeRemoved} onChange={(e) => setFilters({...filters, includeRemoved: e.target.checked})} /> Include removed</label>}
        <button className="primary" disabled={loading}>{loading ? "Searching…" : "Search"}</button>
      </form>

      {error && <p className="error">{error}</p>}

      <div className="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Time</th><th>Status</th><th>Dept.</th><th>Customer</th><th>Vehicle</th><th>Stock #</th><th>Salesperson</th><th>F&amp;I</th>{canManage && <th>Actions</th>}</tr></thead>
          <tbody>{deals.map((deal) => (
            <tr key={deal.id} className={deal.status === "removed" ? "removed-row" : ""}>
              <td>{deal.date}</td><td>{deal.timeLabel}</td><td>{deal.status}</td><td>{deal.departmentName}</td><td>{deal.customer}</td><td>{deal.vehicle}</td><td>{deal.stockNumber}</td><td>{deal.salesperson}</td><td>{deal.fiManager}</td>
              {canManage && <td className="actions">
                {deal.status === "active" && <><button className="link-button" onClick={() => setEditing(deal)}>Edit</button><button className="link-button danger" onClick={() => void remove(deal)}>Remove</button></>}
                {deal.status === "removed" && canRestore && <button className="link-button" onClick={() => void restore(deal)}>Restore</button>}
              </td>}
            </tr>
          ))}</tbody>
        </table>
      </div>

      {!loading && deals.length === 0 && <p className="empty-state">Choose filters and search for a deal.</p>}
    </section>
  );
}
